import React, { useState, useEffect, ReactElement } from 'react';
import { Popover } from 'antd';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { PlusCircleOutlined } from '@ant-design/icons';
import { sendNotification } from 'utils/helperMethods';

const PopoverStyled = styled(Popover)`
    .ant-popover-inner-content {
        padding: 2px !important;
    }
    .ant-popover-content {
        border-radius: 5px;
        box-shadow: 5px 10px 11px 2px;
        overflow: hidden;
    }
`;

export type KPIItem = {
    label: string;
    value: string;
    isSelected: boolean;
};

type KPISelectorProps = {
    isVisible: boolean; // Controls popover visibility
    setIsVisible: (visible: boolean) => void; // Sets popover visibility
    kpiList: KPIItem[]; // Array of KPIs with selection info
    onSubmit: (updatedList: string[]) => void; // Callback when popover closes
    buttonName?: string;
    searchPlaceholder?: string;
    variableName: string;
    hideSearch?: boolean;
    buttonComponent?: ReactElement;
    showSelectAll?: boolean;
};

const KPISelectorV2: React.FC<KPISelectorProps> = ({
    isVisible,
    setIsVisible,
    kpiList,
    onSubmit,
    buttonName = 'Add / Remove KPI',
    variableName,
    searchPlaceholder = 'Search',
    hideSearch = false,
    buttonComponent,
    showSelectAll = false,
}) => {
    const [tempKPIList, setTempKPIList] = useState<KPIItem[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectAll, setSelectAll] = useState<boolean>(false);

    // Sync temporary KPI list with the initial list when it changes
    useEffect(() => {
        setTempKPIList(kpiList);
    }, [kpiList, isVisible]);

    // Toggle selection of a KPI
    const handleToggleSelection = (value: string) => {
        setTempKPIList((prevState) =>
            prevState.map((item) => (item.value === value ? { ...item, isSelected: !item.isSelected } : item)),
        );
    };

    useEffect(() => {
        setSelectAll(tempKPIList.every((i) => i.isSelected));
    }, [tempKPIList]);

    // Close the popover and call the onBlur callback with the updated list
    const handleClose = () => {
        setIsVisible(false);
    };

    const onHandleOkay = () => {
        const updatedList = tempKPIList?.filter((v: KPIItem) => v.isSelected)?.map((v: KPIItem) => v.value);
        if (updatedList.length === 0) {
            return sendNotification('error', `Please select minimum one ${variableName}`);
        }
        onSubmit(updatedList);
        setIsVisible(false);
    };

    const selectUnSelectHandler = () => {
        setTempKPIList((prevState) => prevState.map((item) => ({ ...item, isSelected: !selectAll })));
    };

    const filteredList = tempKPIList?.filter((item) => item?.label?.toLowerCase().includes(searchQuery?.toLowerCase()));

    const content = (
        <div>
            {showSelectAll && (
                <div className="text-right text-purpleFont   mb-1 text-xs   border-borderGreyAl1">
                    <button onClick={selectUnSelectHandler}>{` ${selectAll ? 'Unselect' : 'Select'} All`}</button>
                </div>
            )}
            {/* Search Input */}
            {!hideSearch && (
                <div className="relative ">
                    <SearchOutlined
                        style={{
                            fontSize: '18px',
                            color: '#B0B0B0',
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mb-2 pl-8 pr-2 py-2 border rounded w-full h-full bg-backgroundGreyAlt6 flex items-center border-none"
                    />
                </div>
            )}

            <div className="max-h-32 overflow-y-scroll">
                {filteredList.map((item) => (
                    <div
                        key={item.value}
                        onClick={() => handleToggleSelection(item.value)}
                        className={`px-3  py-1 flex justify-between items-center cursor-pointer rounded mb-1 w-[250px] ${
                            item.isSelected ? 'bg-bgBackground' : ''
                        }`}
                    >
                        <span className="">{item.label}</span>
                        {item.isSelected && <CheckOutlined style={{ color: '#44338A' }} />}
                    </div>
                ))}
            </div>
            <div className="border-t border-borderGreyAl1 mt-2 py-2 flex justify-end items-center gap-2">
                <button
                    type="button"
                    className="rounded-md px-2 py-1 border border-borderGreyAl1"
                    onClick={handleClose}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="rounded-md px-4 py-1 border bg-purpleFont text-white"
                    onClick={onHandleOkay}
                >
                    Okay
                </button>
            </div>
        </div>
    );

    return (
        <PopoverStyled
            content={content}
            trigger="click"
            open={isVisible}
            onOpenChange={(visible) => {
                if (visible) {
                    setIsVisible(visible);
                } else {
                    handleClose();
                }
            }}
            placement="bottomLeft"
            showArrow={false}
            overlayStyle={{ borderRadius: 12, paddingTop: 0 }}
        >
            {buttonComponent ? (
                buttonComponent
            ) : (
                <button
                    onClick={() => setIsVisible(!isVisible)}
                    className={`flex justify-between items-center gap-2 rounded px-2 py-1 ${
                        isVisible ? 'bg-backgroundGreyAlt8' : ''
                    } `}
                >
                    <PlusCircleOutlined /> <span>{buttonName} </span>
                </button>
            )}
        </PopoverStyled>
    );
};

export default KPISelectorV2;
